import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Shield, ExternalLink, ChevronLeft, Zap, AlertCircle, MessageCircle } from "lucide-react";
import { api, fmtINR, loadRazorpayScript } from "@/lib/api";
import useSiteConfig, { whatsappLinkFromCfg } from "@/lib/useSiteConfig";
import { track } from "@/components/common/Analytics";
import PhoneInput from "@/components/common/PhoneInput";
import PolicyDialog from "@/components/common/PolicyDialog";
import HouseRulesDialog from "@/components/common/HouseRulesDialog";
import HouseLoader from "@/components/common/HouseLoader";
import { toast } from "sonner";

const plusDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().split("T")[0]; };

export default function Booking() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const slug = params.get("slug");
  const [property, setProperty] = useState(null);
  const [form, setForm] = useState({
    check_in: params.get("check_in") || plusDays(3),
    check_out: params.get("check_out") || plusDays(5),
    guests: Number(params.get("guests") || 2),
    guest_name: "", guest_email: "", guest_phone: "", note: "",
  });
  const [quote, setQuote] = useState(null);
  const [config, setConfig] = useState({ booking_mode: "hostify_engine_handoff" });
  const [loading, setLoading] = useState(false);
  const [consent, setConsent] = useState(false);
  const cfg = useSiteConfig();

  useEffect(() => {
    if (!slug) return;
    api.get(`/properties/${slug}`).then(({ data }) => setProperty(data));
    api.get("/config").then(({ data }) => setConfig(data));
  }, [slug]);

  useEffect(() => {
    if (!slug) return;
    const run = async () => {
      try {
        const { data } = await api.post("/bookings/quote", {
          slug, start_date: form.check_in, end_date: form.check_out, guests: form.guests,
        });
        setQuote(data);
      } catch (err) {
        setQuote(null);
        toast.error(err.response?.data?.detail || "Unable to fetch quote");
      }
    };
    run();
  }, [slug, form.check_in, form.check_out, form.guests]);

  // Log a booking attempt as soon as the guest starts filling in contact details so we
  // can track the funnel (form_opened → contact_touched → intent_created → confirmed).
  const loggedAttemptRef = useRef(false);
  useEffect(() => {
    if (loggedAttemptRef.current) return;
    if (!form.guest_name && !form.guest_email && !form.guest_phone) return;
    loggedAttemptRef.current = true;
    api.post("/bookings/attempt", {
      stage: "contact_touched",
      slug,
      start_date: form.check_in,
      end_date: form.check_out,
      guests: form.guests,
      guest_name: form.guest_name,
      guest_email: form.guest_email,
      guest_phone: form.guest_phone,
      total: quote?.total,
    }).catch(() => {});
  }, [form.guest_name, form.guest_email, form.guest_phone, slug, form.check_in, form.check_out, form.guests, quote]);

  // Fire begin_checkout once we have a valid quote.
  const loggedCheckoutRef = useRef(false);
  useEffect(() => {
    if (loggedCheckoutRef.current || !quote?.available || !property) return;
    loggedCheckoutRef.current = true;
    track("begin_checkout", {
      content_type: "stay",
      content_ids: [slug],
      content_name: property.display_name,
      value: quote.total,
      currency: "INR",
      num_items: quote.nights,
    });
  }, [quote, property, slug]);

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (!quote || !quote.available) return;
    if (!consent) {
      toast.error("Please acknowledge the policies to continue.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/bookings/intent", {
        slug,
        start_date: form.check_in,
        end_date: form.check_out,
        guests: form.guests,
        guest_name: form.guest_name,
        guest_email: form.guest_email,
        guest_phone: form.guest_phone,
        note: form.note,
      });

      if (data.mode === "hostify_engine_handoff") {
        track("add_payment_info", { method: "hostify_engine" });
        toast.success("Redirecting to secure booking…");
        setTimeout(() => { window.location.href = data.handoff_url; }, 500);
        return;
      }

      // Custom Razorpay checkout
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("Unable to load Razorpay. Please try again.");
      track("add_payment_info", { method: "razorpay", value: quote.total, currency: "INR" });
      const rzp = new window.Razorpay({
        key: data.razorpay.key_id,
        amount: data.razorpay.amount,
        currency: "INR",
        name: "Vyagram Experiences",
        description: property?.public_name || "Stay",
        order_id: data.razorpay.order_id,
        prefill: { name: form.guest_name, email: form.guest_email, contact: form.guest_phone },
        theme: { color: "#0F3B2E" },
        handler: async (resp) => {
          try {
            await api.post("/bookings/verify", {
              booking_id: data.booking_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            nav(`/booking/confirmation?id=${data.booking_id}`);
          } catch {
            toast.error("Payment verification failed. Our team will reach out.");
          }
        },
        modal: { ondismiss: () => setLoading(false) },
      });
      rzp.open();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409) {
        const blocked = detail?.unavailable_dates || [];
        toast.error(`Dates no longer available${blocked.length ? `: ${blocked.slice(0, 3).join(", ")}` : "."}`);
        // Refresh quote so UI reflects the block
        try {
          const { data: q } = await api.post("/bookings/quote", { slug, start_date: form.check_in, end_date: form.check_out, guests: form.guests });
          setQuote(q);
        } catch (refreshErr) {
          // Quote refresh is a best-effort UX nicety; user already saw the
          // primary error toast above. Log so we have a breadcrumb but don't
          // surface a second toast.
          console.warn("Quote refresh after availability error failed:", refreshErr);
        }
      } else {
        toast.error(typeof detail === "string" ? detail : err.message || "Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!slug) return <div className="p-16 text-center"><p className="font-display text-2xl">Pick a stay first.</p><Link to="/stays" className="vyg-link-gold">Browse stays →</Link></div>;
  if (!property) return <HouseLoader />;

  const mode = config.booking_mode;

  return (
    <div data-testid="booking-page" className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-10">
      <Helmet><title>{`Reserve ${property.display_name} · Vyagram`}</title></Helmet>
      <Link to={`/stays/${slug}`} className="inline-flex items-center gap-1 text-sm text-noir/70 hover:text-forest"><ChevronLeft size={14}/> Back to {property.display_name}</Link>
      <h1 className="mt-4 font-display text-4xl sm:text-5xl">Reserve your stay</h1>

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_360px]">
        <form onSubmit={handleConfirm} className="space-y-6 rounded-2xl bg-white p-6 shadow-card sm:p-8">
          <div>
            <h2 className="font-display text-2xl">Your dates</h2>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <Input label="Check in" type="date" min={plusDays(0)} value={form.check_in} onChange={(v) => {
                // Calendar contract: check-out must be strictly after check-in.
                // When check-in changes, auto-snap check-out to (check-in + 1
                // day) if the previous value would now produce a same-day or
                // reversed range. The check-out picker also disables earlier
                // dates via its `min` attribute.
                const next = { ...form, check_in: v };
                if (v) {
                  const co = form.check_out ? new Date(form.check_out) : null;
                  if (!co || co <= new Date(v)) {
                    const d = new Date(v);
                    d.setDate(d.getDate() + 1);
                    next.check_out = d.toISOString().split("T")[0];
                  }
                }
                setForm(next);
              }} testid="book-checkin" />
              <Input label="Check out" type="date" min={(() => {
                if (!form.check_in) return plusDays(1);
                const d = new Date(form.check_in); d.setDate(d.getDate() + 1);
                return d.toISOString().split("T")[0];
              })()} value={form.check_out} onChange={(v) => setForm({ ...form, check_out: v })} testid="book-checkout" />
              <div className="col-span-2">
                <label className="block text-[11px] uppercase tracking-wider text-amber">Guests</label>
                <select value={form.guests} onChange={(e) => setForm({ ...form, guests: Number(e.target.value) })} className="mt-1.5 w-full rounded-lg border border-forest/20 bg-parchment/60 px-4 py-3 focus:border-forest focus:outline-none" data-testid="book-guests">
                  {Array.from({ length: property.guests }, (_, i) => i + 1).map((n) => (<option key={n} value={n}>{n}</option>))}
                </select>
              </div>
            </div>
          </div>

          <div>
            <h2 className="font-display text-2xl">Your details</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <Input label="Full name" value={form.guest_name} onChange={(v) => setForm({ ...form, guest_name: v })} required testid="book-name" />
              <Input label="Email" type="email" value={form.guest_email} onChange={(v) => setForm({ ...form, guest_email: v })} required testid="book-email" />
              <div className="sm:col-span-2">
                <label className="block">
                  <span className="text-[11px] uppercase tracking-wider text-amber">Phone</span>
                  <div className="mt-1.5">
                    <PhoneInput value={form.guest_phone} onChange={(v) => setForm({ ...form, guest_phone: v })} required testid="book-phone" />
                  </div>
                </label>
              </div>
              <div className="sm:col-span-2">
                <Input label="Anything we should know?" value={form.note} onChange={(v) => setForm({ ...form, note: v })} testid="book-note" />
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-forest/15 bg-mist/60 p-4 text-sm text-noir/80">
            <div className="flex items-start gap-2">
              <Shield size={16} className="mt-0.5 text-forest" />
              {mode === "hostify_engine_handoff" ? (
                <p>Reserve continues on our secure booking engine — same prices, same stay, verified payment.</p>
              ) : (
                <p>Secure payment by Razorpay. Your card details never touch our servers.</p>
              )}
            </div>
          </div>

          {quote && quote.available === false && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4" data-testid="booking-unavailable">
              <p className="font-medium text-red-800 flex items-center gap-2"><AlertCircle size={16}/> These dates aren't available.</p>
              {quote.unavailable_dates?.length > 0 && (
                <p className="mt-1 text-xs text-red-700">Blocked: {quote.unavailable_dates.slice(0, 6).join(", ")}{quote.unavailable_dates.length > 6 ? "…" : ""}</p>
              )}
              <a href={whatsappLinkFromCfg(cfg, `Hi Vyagram, I'm looking at ${property?.display_name} but my dates are blocked. Can you suggest alternates?`)} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-sm font-medium text-white">
                <MessageCircle size={14}/> Talk to us
              </a>
            </div>
          )}

          <label className="flex items-start gap-3 rounded-xl border border-forest/15 bg-mist/30 p-4 text-sm text-noir/80" data-testid="consent-row">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              data-testid="consent-checkbox"
              className="mt-1 h-4 w-4 shrink-0 rounded border-forest/40 accent-forest"
            />
            <span>
              I&apos;ve read and agree to the{" "}
              <PolicyDialog
                triggerLabel="Privacy policy"
                title="Privacy policy"
                subtitle="How we handle your data"
                html={cfg.privacy_html}
                testId="consent-privacy"
                triggerClassName="underline underline-offset-2 text-forest hover:text-gold font-medium"
                emptyFallback={<p className="text-noir/70">We collect only what&apos;s required to confirm and host your stay — name, contact details and reservation details. We don&apos;t sell data to third parties. Full policy coming soon.</p>}
              />
              ,{" "}
              <HouseRulesDialog propertyName={property.display_name} slug={`${property.slug}-consent`} html={property.house_rules_html} variant="inline" />
              {" and "}
              <PolicyDialog
                triggerLabel="Stay policy"
                title="Stay policy"
                subtitle="Cancellation, quiet hours, damage & extras"
                html={cfg.stay_policy_html}
                testId="consent-stay"
                triggerClassName="underline underline-offset-2 text-forest hover:text-gold font-medium"
                emptyFallback={<p className="text-noir/70">Cancellation window, quiet hours, extra guest fees, damage deposit and pet policy will be shared once your booking is confirmed. Reach out if you have questions before booking.</p>}
              />
              .
            </span>
          </label>

          <button type="submit" disabled={loading || !quote || quote?.available === false || !consent} className="vyg-cta w-full disabled:opacity-40 disabled:cursor-not-allowed" data-testid="book-submit">
            {loading ? "Processing…" : quote?.available === false ? "Dates unavailable" : mode === "hostify_engine_handoff" ? (<><ExternalLink size={16}/> Continue to secure booking</>) : "Pay & Reserve"}
          </button>
        </form>

        {quote && (
          <aside className="h-fit rounded-2xl border border-forest/15 bg-white p-6 shadow-card lg:sticky lg:top-24">
            <div className="flex items-center justify-between">
              <p className="vyg-eyebrow">Booking summary</p>
              {quote.source === "hostify" && (
                <span className="inline-flex items-center gap-1 rounded-full bg-forest/10 px-2 py-0.5 text-[10px] font-medium text-forest"><Zap size={10}/> Live</span>
              )}
              {quote.source === "estimated" && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber/15 px-2 py-0.5 text-[10px] font-medium text-amber"><AlertCircle size={10}/> Estimate</span>
              )}
            </div>
            <h3 className="mt-2 font-display text-2xl">{property.display_name}</h3>
            <p className="font-accent text-sm text-forest/70">{property.tagline}</p>
            <img src={property.hero_image} alt="" className="mt-4 aspect-[4/3] w-full rounded-lg object-cover" />
            {quote.available !== false ? (
              <div className="mt-5 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-noir/70">{fmtINR(quote.nightly_price)} × {quote.nights} nights</span><span>{fmtINR(quote.subtotal)}</span></div>
                <div className="flex justify-between"><span className="text-noir/70">Cleaning</span><span>{fmtINR(quote.cleaning_fee)}</span></div>
                {quote.taxes > 0 && (
                  <div className="flex justify-between"><span className="text-noir/70">Taxes</span><span>{fmtINR(quote.taxes)}</span></div>
                )}
                <div className="mt-2 flex justify-between border-t border-forest/10 pt-2 font-medium text-forest"><span>Total</span><span className="font-display text-lg">{fmtINR(quote.total)}</span></div>
                {quote.taxes === 0 && (
                  <p className="pt-0.5 text-[11px] text-noir/60">Total is inclusive of all applicable taxes.</p>
                )}
                {quote.security_deposit > 0 && <p className="pt-1 text-[11px] text-noir/60">+ {fmtINR(quote.security_deposit)} refundable deposit (collected separately)</p>}
              </div>
            ) : (
              <p className="mt-5 rounded-lg bg-red-50 p-3 text-sm text-red-700">Dates are blocked. Try another window or reach out on WhatsApp.</p>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required, min, testid }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wider text-amber">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} min={min} data-testid={testid} className="mt-1.5 w-full rounded-lg border border-forest/20 bg-parchment/60 px-4 py-3 focus:border-forest focus:outline-none" />
    </label>
  );
}
